enum GaEvents {
    FormStart = "form_start",
    FormInteraction = "form_interaction",
    PageView = "page",
    FormSuccess = 'form_success',
}

export enum FormName {
    BusinessRegistration = "business_registration",
    ProspectForm = "prospect_form",
    Contact = "contact",
}

type FormInteractionType = 'submit' | 'view' | 'return' | 'error' | 'skip';

type FormFieldError = {
    error_field: string,
    error_type: string,
};

interface FormStartEventData {
    event: GaEvents;
    form_id: string;
    form_name: FormName;
    eventCallback?: () => void;
    ab_test_variant?: string;
    company?: {
        company_id: number,
    };
}

class GTMFormEvent {
    event: GaEvents;
    form_id?: string;
    form_version_id?: string;
    form_name: FormName;
    form_phase_name?: string;
    form_phase?: string;
    form_total_phases?: string;
    form_step_name: string;
    form_step: string;
    form_total_steps: string;
    form_interaction: FormInteractionType;
    ab_test_variant?: string;
    business_category_id?: number;
    error_data?: FormFieldError[];
    company?: {
        company_id: number,
    };

    constructor(event: GaEvents, form_name: FormName) {
        this.event = event;
        this.form_name = form_name;
    }
}

export interface TrackingData {
    audienceType: string;
    pageType: string;
    pageTemplate: string;
    locale: string;
    businessUnit: string;
    brandId: number;
    brandName: number;
    categoryId?: number | null;
    productName?: string | null;
}

export class B2bGATracker {
    public static allowDebugging = false;

    public static sendFormStartEvent(form_name: FormName, company_id?: number, abTest?: string): Promise<void> {
        return new Promise((resolve) => {
            const timeout = setTimeout(() => {
                console.warn("GTM callback did not fire in time");
                resolve();
            }, 500);// fallback in case GTM doesn't fire callback or isn't present

            const formStartEventData = this.buildCompanyEventData(GaEvents.FormStart, form_name, company_id, abTest);
            formStartEventData.eventCallback = () => {
                clearTimeout(timeout);
                resolve();
            };

            (window as any).dataLayer = (window as any).dataLayer || [];
            (window as any).dataLayer.push(formStartEventData);
            this.debugEvent(formStartEventData);
        });
    }

    public static sendPageViewEvent(
        trackingData: TrackingData,
    ): void {

        let pageViewEventData = {
            event: GaEvents.PageView,
            page_location: window.location.href,
            audience_type: trackingData.audienceType,
            page_type: trackingData.pageType,
            page_template: trackingData.pageTemplate,
            locale: trackingData.locale,
            brand_id: trackingData.brandId,
            brand_name: trackingData.brandName,
            business_category_id: trackingData.categoryId,
            business_category_name: trackingData.productName,
            business_unit: trackingData.businessUnit,
        };

        const abVersion = document.body.getAttribute('data-ab');
        if (abVersion) {
            pageViewEventData.ab_test_variant = abVersion;
        }

        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push(pageViewEventData);
        this.debugEvent(pageViewEventData);
    }

    public static sendFormInteractionEvent(
        form_name: FormName,
        form_interaction: FormInteractionType,
        form_step: string,
        form_total_steps: string,
        form_step_name: string,
        abTest?: string,
        business_category_id?: number,
        error_data?: FormFieldError[]
    ): void {
        const formEvent = new GTMFormEvent(GaEvents.FormInteraction, form_name);
        formEvent.form_interaction = form_interaction;
        formEvent.form_id = document.querySelector('[data-b2b-form-authcode]')
            ?.getAttribute('data-b2b-form-authcode') ?? '';
        formEvent.form_step = form_step;
        formEvent.form_total_steps = form_total_steps;
        formEvent.form_step_name = form_step_name;

        if (abTest) {
            formEvent.ab_test_variant = abTest;
        }

        if (business_category_id !== undefined) {
            formEvent.business_category_id = business_category_id;
        }

        if (error_data !== undefined && form_interaction === 'error') {
            formEvent.error_data = error_data;
        }

        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push(formEvent);
        this.debugEvent(formEvent);
    }

    private static buildCompanyEventData(
        event: GaEvents,
        form_name: FormName,
        company_id?: number,
        abTest?: string,
    ): FormStartEventData {
        const eventData: FormStartEventData = {
            event,
            form_id: document.querySelector('[data-b2b-form-authcode]')
                ?.getAttribute('data-b2b-form-authcode') ?? '',
            form_name,
        };

        if (company_id !== undefined) {
            eventData.company = {company_id};
        }
        if (abTest) {
            eventData.ab_test_variant = abTest;
        }

        return eventData;
    }

    public static listenToB2BRegistrationCTAClick(): void {
        const registrationLinks = document.querySelectorAll('a[href*="/companies/registration"]');

        registrationLinks.forEach(link => {
            link.addEventListener('click', () => {
                B2bGATracker.sendFormStartEvent(FormName.BusinessRegistration)
                    .catch(error => console.error('Error handling registration link click:', error));
            });
        });
    }

    private static debugEvent(event: Record<string, any>): void {
        try {
            if (!this.allowDebugging || !window.localStorage.getItem('ga-debug')) {
                return;
            }

            // eslint-disable-next-line no-console
            console.info('GA event pushed:', event);
        } catch(e) {
            // Storage blocked in this context (e.g. Safari with cookies disabled, private mode)
        }
    }
}

try {
    if (window.location.hash === '#enable-ga-debug') {

        window.localStorage.setItem('ga-debug', '1');
        // eslint-disable-next-line no-console
        console.info('GA debug enabled');
    }
} catch(e) {
    // Storage blocked in this context (e.g. Safari with cookies disabled, private mode)
}

(<any>window).B2bGATracker = B2bGATracker;
window.addEventListener('load', () => {
    B2bGATracker.listenToB2BRegistrationCTAClick();
});
